import { SYSTEM_MODE, SHARED_MODE, SPECIAL_MODE } from '../context/enums';

export const PRIMING_SYSTEM = 'Priming System';
export const COLLABORATIVE_PRIMING = 'Collaborative Priming';
export const AUTONOMY_SUPPRESSED_LABEL = 'Autonomy Suppressed';
export const COLLABORATIVE_DEFENSE_LABEL = 'Collaborative Defense';
export const PRESOAKING_LABEL = 'Presoaking';
export const NON_EMERGENCY_COOLDOWN_LABEL = 'System Ready - Collaboration Suppressed';
const COLLABORATIVE_DEFENSE_ACTIVATING = 'Active: Collaborative Defense Activating';
const COLLABORATIVE_PRIMING_IN_PROGRESS = 'Active: Collaborative Priming In Progress';
const COLLABORATIVE_DEFENSE_ENGAGED = 'Active: Collaborative Defense Engaged';
const PRIMING_IN_PROGRESS = 'Active: Priming In Progress';
const PRESOAKING_IN_PROGRESS = 'Active: Presoaking In Progress';
const MANUAL_DEFENSE_ENGAGED = 'Active: Manual Defense Engaged';
const AUTO_DEFENSE_ENGAGED = 'Active: Automatic Defense Engaged';

/**
 * Nested lookup map from (sharedMode → specialMode → systemMode) to display text.
 * Entries that don't vary by specialMode or systemMode are flat strings.
 * Consumed exclusively by getStatusText.
 */
const STATUS_TEXT = {
  [SHARED_MODE.OFF]: {
    [SPECIAL_MODE.PRESOAKING]: 'Initiating Presoak...',
    [SPECIAL_MODE.NON_EMERGENCY_COOLDOWN]: NON_EMERGENCY_COOLDOWN_LABEL,
    [SPECIAL_MODE.NONE]: {
      [SYSTEM_MODE.AUTO]: 'System Ready',
      [SYSTEM_MODE.MANUAL]: 'Autonomous System Suppressed - Manual Activation Only',
    },
  },
  [SHARED_MODE.PRIMING]: {
    [SPECIAL_MODE.COLLABORATIVE_SOAK]: 'Collaborative Priming In Progress',
    [SPECIAL_MODE.PRESOAKING]: PRIMING_SYSTEM,
    [SPECIAL_MODE.NONE]: PRIMING_SYSTEM,
  },
  [SHARED_MODE.ON]: {
    [SPECIAL_MODE.PRESOAKING]: 'Presoaking Active',
    [SPECIAL_MODE.COLLABORATIVE_SOAK]: 'Collaborative Defense Active',
    [SPECIAL_MODE.NONE]: {
      [SYSTEM_MODE.MANUAL]: 'System Active',
      [SYSTEM_MODE.AUTO]: 'Auto Activation',
    },
  },
  [SHARED_MODE.ACTIVATING]: {
    [SPECIAL_MODE.COLLABORATIVE_SOAK]: 'Collaborative Activation...',
    [SPECIAL_MODE.NONE]: 'Activating System...',
  },
  [SHARED_MODE.DEACTIVATING]: {
    [SPECIAL_MODE.PRESOAKING]: 'Stopping Presoak...',
    [SPECIAL_MODE.COLLABORATIVE_SOAK]: 'Stopping Collaboration...',
    [SPECIAL_MODE.NONE]: 'Stopping Activation...',
  },
  [SHARED_MODE.ERROR]: 'System Error',
  [SHARED_MODE.UNINITIALIZED]: 'System Initializing',
};

/**
 * Returns the display text for a given system status object.
 * @param {Object|null} systemStatus - The structured system status, or null when disconnected.
 * @returns {string} Human-readable status text for the UI.
 */
export function getStatusText(systemStatus) {
  if (!systemStatus) return 'No Device Connected';
  const { systemMode, sharedMode, specialMode } = systemStatus;
  const modeEntry = STATUS_TEXT[sharedMode];
  if (typeof modeEntry === 'string') return modeEntry;
  if (!modeEntry) return 'System Unknown';
  const specialEntry = modeEntry[specialMode];
  if (typeof specialEntry === 'string') return specialEntry;
  if (!specialEntry) return 'System Unknown';
  return specialEntry[systemMode] ?? 'System Unknown';
}

/**
 * Returns true when autonomous activation is suppressed by manual override, independent
 * of whether the suppression is time-limited or indefinite.
 * @param {Object|null} systemStatus - The structured system status.
 * @returns {boolean}
 */
export function isAutonomySuppressed(systemStatus) {
  return systemStatus?.systemMode === SYSTEM_MODE.MANUAL
    && systemStatus?.sharedMode === SHARED_MODE.OFF
    && systemStatus?.specialMode === SPECIAL_MODE.NONE;
}

/**
 * Returns true when autonomous collaboration is suppressed by a non-emergency cooldown,
 * independent of whether the suppression is time-limited or indefinite.
 * @param {Object|null} systemStatus - The structured system status.
 * @returns {boolean}
 */
export function isNonEmergencyCooldownActive(systemStatus) {
  return systemStatus?.sharedMode === SHARED_MODE.OFF
    && systemStatus?.specialMode === SPECIAL_MODE.NON_EMERGENCY_COOLDOWN;
}

/**
 * Returns true when a collaborative defense is actively engaged (sharedMode ON), as opposed
 * to collaborative activation/priming which precede it.
 * @param {Object|null} systemStatus - The structured system status.
 * @returns {boolean}
 */
export function isCollaborativeDefenseEngaged(systemStatus) {
  return systemStatus?.sharedMode === SHARED_MODE.ON
    && systemStatus?.specialMode === SPECIAL_MODE.COLLABORATIVE_SOAK;
}

/**
 * Returns true when presoaking is actively engaged (sharedMode ON), as opposed to
 * presoak priming or the initiating/stopping transitional states.
 * @param {Object|null} systemStatus - The structured system status.
 * @returns {boolean}
 */
export function isPresoakingEngaged(systemStatus) {
  return systemStatus?.sharedMode === SHARED_MODE.ON
    && systemStatus?.specialMode === SPECIAL_MODE.PRESOAKING;
}

/**
 * Nested lookup map from (sharedMode → specialMode → systemMode) to active-state banner text.
 * Returns a string leaf for states with a banner, or undefined for states with no banner.
 * Consumed exclusively by getActiveBannerMessage.
 */
const BANNER_TEXT = {
  [SHARED_MODE.ACTIVATING]: {
    [SPECIAL_MODE.COLLABORATIVE_SOAK]: COLLABORATIVE_DEFENSE_ACTIVATING,
  },
  [SHARED_MODE.PRIMING]: {
    [SPECIAL_MODE.COLLABORATIVE_SOAK]: COLLABORATIVE_PRIMING_IN_PROGRESS,
    [SPECIAL_MODE.PRESOAKING]: PRIMING_IN_PROGRESS, 
    [SPECIAL_MODE.NONE]: PRIMING_IN_PROGRESS,
  },
  [SHARED_MODE.ON]: {
    [SPECIAL_MODE.COLLABORATIVE_SOAK]: COLLABORATIVE_DEFENSE_ENGAGED,
    [SPECIAL_MODE.PRESOAKING]: PRESOAKING_IN_PROGRESS,
    [SPECIAL_MODE.NONE]: {
      [SYSTEM_MODE.MANUAL]: MANUAL_DEFENSE_ENGAGED,
      [SYSTEM_MODE.AUTO]: AUTO_DEFENSE_ENGAGED,
    },
  },
};

/**
 * Returns the active-state banner message for a given system status object.
 * Resolves sharedMode → specialMode → systemMode through BANNER_TEXT; returns null
 * for any state not represented in the table (system is not in an active/warning state).
 * @param {Object|null} systemStatus - The structured system status.
 * @returns {string|null} Banner message string, or null if no banner applies.
 */
export function getActiveBannerMessage(systemStatus) {
  if (!systemStatus) return null;
  const { systemMode, sharedMode, specialMode } = systemStatus;
  // sharedMode not in table — no banner for this state (e.g. OFF, DEACTIVATING)
  const sharedEntry = BANNER_TEXT[sharedMode]; 
  if (!sharedEntry) return null;
  // specialMode not in table — no banner for this combination
  const specialEntry = sharedEntry[specialMode]; 
  // leaf is a direct string — no systemMode lookup needed
  if (typeof specialEntry === 'string') return specialEntry; 
  if (!specialEntry) return null;
  // leaf is a systemMode sub-table; ?? null guards unrecognized systemMode values
  return specialEntry[systemMode] ?? null; 
}
